/* =====================================================================
   product-split.js  (CORE, loaded with `defer`)
   Runs right away: accordion, qty, add to cart, enquiry popup, WhatsApp, gallery swipe + dots, share,
   bundle, set picker, offers, stock timer.
   Runs when the browser is idle: sticky columns / sticky cart bar, off-screen animation pausing.
   Fetched only when needed (see PS.load):
     product-split-viewer.js   full screen gallery viewer + pinch zoom (after page load, or first tap on a photo)
     product-split-reviews.js  reviews tabs / popup / photo viewer (when the reviews come near the screen)
     product-split-3d.js       3D viewer (when the customer touches / hovers "View 3D")
   ===================================================================== */
/* =====================================================================
   SHARED HELPERS (used here and by the on-demand modules)
   Defined first, because the set picker / offers code below calls them synchronously.
   ===================================================================== */
(function () {
  var PS = window.PS = window.PS || {};
  /* swipe down on the handle / header to close a bottom sheet (phones only) */
  PS.swipeToClose = PS.swipeToClose || function (sheet, handles, close) {
    if (!sheet) return;
    var startY = null, dy = 0;
    function start(e) {
      if (window.innerWidth > 900) return;
      startY = e.touches[0].clientY; dy = 0;
      sheet.style.transition = 'none';
    }
    function move(e) {
      if (startY === null) return;
      dy = Math.max(0, e.touches[0].clientY - startY);
      sheet.style.transform = 'translate(-50%, ' + dy + 'px)';
    }
    function end() {
      if (startY === null) return;
      var shouldClose = dy > 90;
      startY = null;
      if (shouldClose) {
        sheet.style.transition = 'transform .2s ease';
        sheet.style.transform = 'translate(-50%, 100%)';
        setTimeout(function () { close(); sheet.style.transform = ''; sheet.style.transition = ''; }, 190);
      } else {
        sheet.style.transition = 'transform .25s ease';
        sheet.style.transform = '';
        setTimeout(function () { sheet.style.transition = ''; }, 260);
      }
    }
    (handles || []).forEach(function (el) {
      if (!el) return;
      el.addEventListener('touchstart', start, { passive: true });
      el.addEventListener('touchmove', move, { passive: true });
      el.addEventListener('touchend', end);
      el.addEventListener('touchcancel', end);
    });
  };
  /* keep Tab focus inside a dialog */
  PS.trapFocus = PS.trapFocus || function (e, container) {
    var f = [].slice.call(container.querySelectorAll('button, a[href], video[controls], iframe, [tabindex]:not([tabindex="-1"])')).filter(function (el) {
      return !el.disabled && !el.hidden && el.offsetParent !== null;
    });
    if (!f.length) return;
    var first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  };
})();

(function () {
  var reduceMotion = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  var noop = function () {};
  var idle = window.requestIdleCallback
    ? function (fn, t) { window.requestIdleCallback(fn, { timeout: t || 1500 }); }
    : function (fn) { setTimeout(fn, 200); };

  /* ---------- ON-DEMAND MODULE LOADER ----------
     The script URLs come from data-js-* attributes on .product-split (Liquid asset_url).
     Each module registers itself with PS.define(name, api). */
  var PS = window.PS = window.PS || {};
  PS.mod = PS.mod || {};
  PS.define = PS.define || function (name, api) { PS.mod[name] = api || {}; };
  var host = document.querySelector('.product-split');
  var pending = {};
  PS.load = function (name) {
    if (pending[name]) return pending[name];
    if (PS.mod[name]) return (pending[name] = Promise.resolve(PS.mod[name]));
    var url = host && host.getAttribute('data-js-' + name);
    if (!url) return Promise.reject(new Error('no ' + name));
    pending[name] = new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = url;
      s.async = true;
      s.onload = function () { resolve(PS.mod[name] || {}); };
      s.onerror = function () {
        delete pending[name];
        if (s.parentNode) s.parentNode.removeChild(s);
        reject(new Error(name));
      };
      document.head.appendChild(s);
    });
    return pending[name];
  };

  /* ---------- ACCORDION (smooth height animation, one open at a time) ---------- */
  (function () {
    var items = document.querySelectorAll('.product-accordion .accordion-item');
    if (!items.length) return;
    var DURATION = 320;
    var EASE = 'cubic-bezier(.22, 1, .36, 1)';
    var COLLAPSED = { height: '0px', paddingTop: '0px', paddingBottom: '0px', opacity: 0 };

    function snapshot(el) {
      var cs = getComputedStyle(el);
      return { height: el.getBoundingClientRect().height + 'px', paddingTop: cs.paddingTop, paddingBottom: cs.paddingBottom, opacity: cs.opacity };
    }
    function play(item, content, from, to, done) {
      if (reduceMotion || !content.animate) { done(); return; }
      var anim = content.animate([from, to], { duration: DURATION, easing: EASE });
      item._anim = anim;
      anim.onfinish = function () { if (item._anim !== anim) return; item._anim = null; done(); };
      anim.oncancel = function () { if (item._anim === anim) item._anim = null; };
    }
    function expand(item) {
      var content = item.querySelector('.accordion-content');
      if (!content) { item.open = true; return; }
      var from = item._anim ? snapshot(content) : COLLAPSED;
      if (item._anim) { item._anim.cancel(); item._anim = null; }
      item.classList.remove('is-closing');
      item.open = true;
      var to = snapshot(content);
      play(item, content, from, to, function () {
        var top = item.getBoundingClientRect().top;
        if (top < 0) item.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
      });
    }
    function collapse(item) {
      var content = item.querySelector('.accordion-content');
      if (!content) { item.open = false; return; }
      var from = snapshot(content);
      if (item._anim) { item._anim.cancel(); item._anim = null; }
      item.classList.add('is-closing');
      play(item, content, from, COLLAPSED, function () { item.open = false; item.classList.remove('is-closing'); });
    }
    function isShown(item) { return item.open && !item.classList.contains('is-closing'); }
    items.forEach(function (item) {
      var summary = item.querySelector('summary');
      if (!summary) return;
      summary.addEventListener('click', function (e) {
        e.preventDefault();
        if (isShown(item)) collapse(item);
        else {
          items.forEach(function (other) { if (other !== item && isShown(other)) collapse(other); });
          expand(item);
        }
      });
    });
  })();

  /* ---------- QUANTITY ---------- */
  document.querySelectorAll('.qty-pill').forEach(function (qty) {
    var input = qty.querySelector('input');
    if (!input) return;
    function read() { var n = parseInt(input.value, 10); return n > 0 ? n : 1; }
    function bump() {
      if (reduceMotion) return;
      input.classList.remove('is-bump'); void input.offsetWidth; input.classList.add('is-bump');
    }
    qty.querySelector('.plus').addEventListener('click', function () { input.value = read() + 1; bump(); });
    qty.querySelector('.minus').addEventListener('click', function () { var c = read(); input.value = c > 1 ? c - 1 : 1; bump(); });
    input.addEventListener('change', function () { input.value = read(); });
    input.addEventListener('blur', function () { input.value = read(); });
  });

  /* ---------- ADD TO CART / BUY NOW: instant feedback + Meta Pixel AddToCart ---------- */
  (function () {
    var form = document.querySelector('.product-form');
    if (!form) return;
    var busy = null, resetTimer = null, passThrough = false;
    function reset() { clearTimeout(resetTimer); if (busy) { busy.classList.remove('loading'); busy = null; } }
    function trackAddToCart() {
      try {
        if (typeof window.fbq !== 'function') return;
        var d = form.dataset;
        var priceEl = document.getElementById('mainPriceSp');
        var value = priceEl ? parseFloat((priceEl.textContent || '').replace(/[^0-9.]/g, '')) : NaN;
        var qtyEl = form.querySelector('input[name="quantity"]');
        var qty = qtyEl ? parseInt(qtyEl.value, 10) || 1 : 1;
        var idEl = form.querySelector('input[name="id"]');
        var id = String(d.productId || (idEl && idEl.value) || '');
        var data = { content_type: 'product', content_ids: [id], content_name: d.productTitle || document.title, contents: [{ id: id, quantity: qty }], currency: d.currency || 'INR' };
        if (!isNaN(value)) data.value = value * qty;
        window.fbq('track', 'AddToCart', data);
      } catch (err) {}
    }
    form.addEventListener('submit', function (e) {
      if (passThrough) return;
      var btn = e.submitter;
      if (!btn || !btn.classList || btn.disabled) return;
      if (!btn.classList.contains('btn-add-to-cart') && !btn.classList.contains('btn-buy-now')) return;
      reset();
      busy = btn;
      btn.classList.add('loading');
      resetTimer = setTimeout(reset, 4500);
      if (btn.classList.contains('btn-add-to-cart') && typeof window.fbq === 'function') {
        e.preventDefault();
        trackAddToCart();
        setTimeout(function () {
          passThrough = true;
          if (form.requestSubmit) form.requestSubmit(btn); else form.submit();
        }, 180);
      }
    });
    window.addEventListener('pageshow', function (e) { if (e.persisted) { passThrough = false; reset(); } });
  })();

  /* ---------- WHATSAPP HELP: add the selected set + quantity to the message ---------- */
  (function () {
    var wa = document.getElementById('waHelp');
    if (!wa) return;
    wa.addEventListener('click', function () {
      var d = wa.dataset;
      var setInput = document.getElementById('setPropertyInput');
      var qtyInput = document.querySelector('.product-form input[name="quantity"]');
      var text = (d.msg || 'Hi! I need help with this product') + ': ' + d.title;
      if (setInput && setInput.value) text += ' (' + setInput.value + ')';
      if (qtyInput && parseInt(qtyInput.value, 10) > 1) text += ' x' + qtyInput.value;
      text += ' - ' + d.url;
      wa.href = 'https://wa.me/' + d.phone + '?text=' + encodeURIComponent(text);
    });
  })();

  /* ---------- ENQUIRY MODE (custom.enable_enquiry = True): popup -> WhatsApp ----------
     "Enquire now" opens the popup (bottom sheet on phones) with one message box.
     "Send enquiry on WhatsApp" opens WhatsApp with the customer's message + product name, set, price and
     product link, typed to the store's number (data-wa on #enqModal); the customer taps Send there.
     If the browser blocks the new tab, the "Open WhatsApp" button in the done view opens it. */
  (function () {
    var modal = document.getElementById('enqModal');
    var form = document.getElementById('enqForm');
    if (!modal || !form) return;
    var sheet = modal.querySelector('.enq__sheet');
    var body = document.getElementById('enqBody');
    var closeBtn = document.getElementById('enqClose');
    var doneBtn = document.getElementById('enqDoneBtn');
    var waBtn = document.getElementById('enqWaBtn');
    var msgEl = document.getElementById('enqMsg');
    var setInput = document.getElementById('enqSetInput');
    var setLabel = document.getElementById('enqSetLabel');
    var priceEl = document.getElementById('enqPrice');
    var waNumber = (modal.getAttribute('data-wa') || '').replace(/\D/g, '');
    var defaultMsg = msgEl ? msgEl.value : '';
    var lastTrigger = null, focusTimer = null, unlockTimer = null;

    function isOpen() { return modal.classList.contains('open'); }
    function focusEl(el) { if (!el) return; try { el.focus({ preventScroll: true }); } catch (e) { el.focus(); } }
    function open(trigger) {
      if (isOpen()) return;
      lastTrigger = trigger || null;
      clearTimeout(unlockTimer); clearTimeout(focusTimer);
      var sbw = window.innerWidth - document.documentElement.clientWidth;
      if (sbw > 0) document.body.style.paddingRight = sbw + 'px';
      document.body.style.overflow = 'hidden';
      modal.setAttribute('aria-hidden', 'false');
      void modal.offsetWidth;
      modal.classList.add('open');
      /* desktop: straight into the message box; phones: the close button (no keyboard jumping up) */
      focusTimer = setTimeout(function () { focusEl(window.innerWidth > 900 ? msgEl : closeBtn); }, 80);
    }
    function close() {
      if (!isOpen()) return;
      clearTimeout(focusTimer);
      modal.classList.remove('open');
      modal.setAttribute('aria-hidden', 'true');
      unlockTimer = setTimeout(function () {
        if (isOpen()) return;
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';
        /* after a sent enquiry, the next open starts fresh */
        if (modal.classList.contains('is-done')) {
          modal.classList.remove('is-done');
          if (msgEl) msgEl.value = defaultMsg;
        }
      }, 300);
      focusEl(lastTrigger);
    }

    function buildMessage() {
      var note = msgEl ? (msgEl.value || '').trim() : '';
      if (!note) note = defaultMsg;
      var product = 'Product: ' + (modal.getAttribute('data-title') || document.title);
      if (setInput && setInput.value) product += ' (' + setInput.value + ')';
      var lines = [note, '', product];
      var price = priceEl ? (priceEl.textContent || '').trim() : '';
      if (price) lines.push('Price: ' + price);
      var url = modal.getAttribute('data-url');
      if (url) lines.push(url);
      return lines.join('\n');
    }
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var link = 'https://wa.me/' + waNumber + '?text=' + encodeURIComponent(buildMessage());
      if (waBtn) waBtn.href = link;
      /* must run inside the tap, or the browser blocks the new tab */
      try {
        var w = window.open(link, '_blank');
        if (w) w.opener = null;
      } catch (err) {}
      modal.classList.add('is-done');
      if (body) body.scrollTop = 0;
      setTimeout(function () { focusEl(waBtn || doneBtn); }, 60);
      try {
        if (typeof window.fbq === 'function') {
          window.fbq('track', 'Lead', { content_name: document.title, content_category: 'product_enquiry' });
        }
      } catch (err) {}
    });

    /* keep the selected set + its price in the enquiry */
    document.addEventListener('set:change', function (e) {
      var d = e.detail || {};
      if (setInput) setInput.value = d.title || '';
      if (setLabel) { setLabel.textContent = d.title || ''; setLabel.hidden = !d.title; }
      if (priceEl && d.sp) priceEl.textContent = d.sp;
    });

    var openers = [document.getElementById('enqOpen')].concat([].slice.call(document.querySelectorAll('[data-enq-open]')));
    openers.forEach(function (btn) {
      if (!btn) return;
      btn.addEventListener('click', function (e) { e.preventDefault(); open(btn); });
    });
    closeBtn.addEventListener('click', close);
    if (doneBtn) doneBtn.addEventListener('click', close);
    modal.addEventListener('click', function (e) { if (e.target === modal) close(); });
    document.addEventListener('keydown', function (e) {
      if (!isOpen()) return;
      if (e.key === 'Escape') { close(); return; }
      if (e.key === 'Tab') PS.trapFocus(e, sheet);
    });
    PS.swipeToClose(sheet, [document.getElementById('enqGrab'), document.getElementById('enqHead')], close);
  })();

  /* ---------- GALLERY: pagination (dots, or "2 / 9") + mouse drag ---------- */
  document.querySelectorAll('.image-slider-wrapper').forEach(function (wrapper) {
    var slider = wrapper.querySelector('.image-slider');
    var pagination = wrapper.querySelector('.slider-pagination');
    if (!slider) return;
    var slideEls = [].slice.call(slider.querySelectorAll('.slide'));
    var total = slideEls.length;

    function goTo(idx) {
      if (!slideEls[idx]) return;
      slider.scrollTo({ left: slideEls[idx].offsetLeft, behavior: reduceMotion ? 'auto' : 'smooth' });
    }

    if (pagination) {
      if (total < 2) {
        pagination.hidden = true;
      } else {
        var dots = [];
        var countEl = null;
        var MAX_DOTS = 5;
        var setActive = function (index) {
          dots.forEach(function (d, i) {
            var on = i === index;
            d.classList.toggle('active', on);
            if (on) d.setAttribute('aria-current', 'true'); else d.removeAttribute('aria-current');
          });
          if (countEl) countEl.textContent = (index + 1) + ' / ' + total;
        };
        var frag = document.createDocumentFragment();
        if (total > MAX_DOTS) {
          pagination.classList.add('is-count');
          countEl = document.createElement('span');
          countEl.className = 'slider-pagination__count';
          countEl.setAttribute('aria-live', 'polite');
          frag.appendChild(countEl);
        } else {
          slideEls.forEach(function (slide, idx) {
            var dot = document.createElement('button');
            dot.type = 'button';
            dot.className = 'dot';
            dot.setAttribute('aria-label', 'Go to image ' + (idx + 1) + ' of ' + total);
            dot.addEventListener('click', function () { goTo(idx); setActive(idx); });
            frag.appendChild(dot);
            dots.push(dot);
          });
        }
        pagination.appendChild(frag);
        setActive(0);
        if ('IntersectionObserver' in window) {
          var io = new IntersectionObserver(function (entries) {
            entries.forEach(function (entry) { if (entry.isIntersecting) setActive(slideEls.indexOf(entry.target)); });
          }, { root: slider, threshold: 0.6 });
          slideEls.forEach(function (s) { io.observe(s); });
        }
      }
    }

    var isDown = false, moved = false, startX = 0, startLeft = 0;
    function onDragMove(e) {
      if (!isDown) return;
      var dx = e.pageX - startX;
      if (Math.abs(dx) > 5) moved = true;
      slider.scrollLeft = startLeft - dx;
    }
    function endDrag() {
      if (!isDown) return;
      isDown = false;
      window.removeEventListener('mousemove', onDragMove);
      window.removeEventListener('mouseup', endDrag);
      slider.classList.remove('is-dragging');
      if (!moved) return;
      var best = 0, bestDist = Infinity;
      slideEls.forEach(function (s, i) { var d = Math.abs(s.offsetLeft - slider.scrollLeft); if (d < bestDist) { bestDist = d; best = i; } });
      goTo(best);
    }
    slider.addEventListener('mousedown', function (e) {
      if (e.button !== 0 || slider.scrollWidth <= slider.clientWidth + 1) return;
      isDown = true; moved = false;
      startX = e.pageX; startLeft = slider.scrollLeft;
      slider.classList.add('is-dragging');
      window.addEventListener('mousemove', onDragMove);
      window.addEventListener('mouseup', endDrag);
    });
    slider.addEventListener('dragstart', function (e) { e.preventDefault(); });
    /* a drag must not count as a click (it would open the image viewer) */
    slider.addEventListener('click', function (e) {
      if (moved) { e.stopPropagation(); e.preventDefault(); moved = false; }
    }, true);
  });

  /* ---------- GALLERY -> FULL SCREEN VIEWER (viewer code is fetched on demand) ---------- */
  (function () {
    var slides = [].slice.call(document.querySelectorAll('.image-slider .slide'));
    if (!slides.length || !document.getElementById('imageModal')) return;
    function warm() { PS.load('viewer').catch(noop); }
    function open(index, slide) {
      PS.load('viewer').then(function (m) { if (m.open) m.open(index, slide); }).catch(noop);
    }
    slides.forEach(function (slide, index) {
      if (!slide.querySelector('img')) return;
      slide.setAttribute('role', 'button');
      slide.setAttribute('tabindex', '0');
      slide.setAttribute('aria-label', 'Open image ' + (index + 1) + ' of ' + slides.length + ' full screen');
      slide.addEventListener('pointerdown', warm, { passive: true, once: true });
      slide.addEventListener('click', function (e) {
        if (e.target.closest && e.target.closest('video, iframe, model-viewer')) return;
        open(index, slide);
      });
      slide.addEventListener('keydown', function (e) {
        if ((e.key === 'Enter' || e.key === ' ') && e.target === slide) { e.preventDefault(); open(index, slide); }
      });
    });
    /* fetch it quietly once the page has finished loading, so the first tap is instant */
    function later() { idle(warm, 4000); }
    if (document.readyState === 'complete') later(); else window.addEventListener('load', later, { once: true });
  })();

  /* ---------- 3D VIEWER (fetched on intent) ---------- */
  (function () {
    var btn = document.getElementById('m3dOpen');
    if (!btn) return;
    function warm() { PS.load('3d').catch(noop); }
    ['pointerenter', 'touchstart', 'focus'].forEach(function (t) { btn.addEventListener(t, warm, { passive: true, once: true }); });
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      PS.load('3d').then(function (m) { if (m.open) m.open(); }).catch(noop);
    });
  })();

  /* ---------- REVIEWS (fetched when the block comes near the screen) ---------- */
  (function () {
    var root = document.getElementById('customerReviews');
    var sp = document.getElementById('socialProof');
    if (root) {
      var started = false;
      var start = function () { if (started) return; started = true; PS.load('reviews').catch(function () { started = false; }); };
      if ('IntersectionObserver' in window) {
        var rio = new IntersectionObserver(function (entries) {
          if (entries.some(function (en) { return en.isIntersecting; })) { rio.disconnect(); start(); }
        }, { rootMargin: '800px 0px' });
        rio.observe(root);
      } else {
        idle(start, 3000);
      }
      /* any tap inside the block before the code arrived starts loading too */
      root.addEventListener('pointerdown', start, { passive: true, once: true });
    }
    if (sp && sp.tagName === 'A' && root) {
      sp.addEventListener('click', function (e) {
        e.preventDefault();
        root.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
        PS.load('reviews').then(function (m) { if (m.setTab) m.setTab('all'); }).catch(noop);
      });
    }
  })();

  /* ---------- SHARE ---------- */
  (function () {
    var shareBtn = document.getElementById('mediaShareBtn');
    if (!shareBtn) return;
    var label = document.getElementById('mediaShareText');
    var timer = null;
    function flash(text) {
      if (!label) return;
      label.textContent = text;
      clearTimeout(timer);
      timer = setTimeout(function () { label.textContent = 'Share'; }, 1600);
    }
    shareBtn.addEventListener('click', function () {
      var url = window.location.href;
      if (navigator.share) navigator.share({ title: document.title, url: url }).catch(noop);
      else if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(url).then(function () { flash('Copied'); }, function () { flash('Copy failed'); });
      else flash('Copy failed');
    });
  })();

  /* ---------- BUNDLE ADD TO CART ---------- */
  (function () {
    var bundleBtn = document.getElementById('addBundleBtn');
    if (!bundleBtn) return;
    var original = bundleBtn.textContent;
    bundleBtn.addEventListener('click', function () {
      if (bundleBtn.disabled) return;
      var items = [];
      document.querySelectorAll('.bundle-variant').forEach(function (v) { if (v.value) items.push({ id: v.value, quantity: 1 }); });
      if (!items.length) return;
      bundleBtn.disabled = true;
      bundleBtn.textContent = 'ADDING…';
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: items })
      }).then(function (res) {
        if (!res.ok) throw new Error('cart');
        window.location.href = '/cart';
      }).catch(function () {
        bundleBtn.textContent = 'TRY AGAIN';
        setTimeout(function () { bundleBtn.disabled = false; bundleBtn.textContent = original; }, 1800);
      });
    });
  })();

  /* ---------- SET PICKER (Choose your set) + "See details" popup ---------- */
  (function () {
    var picker = document.getElementById('setPicker');
    if (!picker) return;
    var propInput = document.getElementById('setPropertyInput');
    var radios = picker.querySelectorAll('.set-option__radio');
    var toggleBtn = document.getElementById('setPickerToggle');
    var selectedName = document.getElementById('setPickerSelectedName');
    var selectedPrice = document.getElementById('setPickerSelectedPrice');
    if (toggleBtn) {
      toggleBtn.addEventListener('click', function () {
        var open = picker.classList.toggle('is-open');
        toggleBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    }

    function selectSet(radio) {
      radio.checked = true;
      if (selectedName) selectedName.textContent = radio.dataset.title || '';
      if (selectedPrice) selectedPrice.textContent = radio.dataset.price || '';
      picker.querySelectorAll('.set-option').forEach(function (o) { o.classList.remove('is-selected'); });
      radio.closest('.set-option').classList.add('is-selected');
      var d = radio.dataset;
      var el;
      if ((el = document.getElementById('mainPriceSp'))) {
        el.textContent = d.sp || '';
        if (!reduceMotion) { el.classList.remove('is-bump'); void el.offsetWidth; el.classList.add('is-bump'); }
      }
      if ((el = document.getElementById('mainPriceMrp'))) { el.textContent = d.mrp || ''; el.hidden = !d.mrp; }
      if ((el = document.getElementById('mainPriceSave'))) { el.textContent = d.save || ''; el.hidden = !d.save; }
      if (propInput) propInput.value = radio.dataset.title || '';
      if (radio.dataset.variant) {
        document.querySelectorAll('input[name="id"]').forEach(function (i) { i.value = radio.dataset.variant; });
      }
      try { document.dispatchEvent(new CustomEvent('set:change', { detail: Object.assign({}, radio.dataset) })); } catch (err) {}
    }
    radios.forEach(function (r) { r.addEventListener('change', function () { selectSet(r); }); });

    var modal = document.getElementById('setDetailsModal');
    if (!modal) return;
    var modalBody = document.getElementById('setModalBody');
    var modalTitle = document.getElementById('setModalTitle');
    var selectBtn = document.getElementById('setModalSelect');
    var selectName = document.getElementById('setModalSelectName');
    var selectPrice = document.getElementById('setModalSelectPrice');
    var closeBtn = document.getElementById('setModalClose');
    var sheet = modal.querySelector('.set-modal__sheet');
    var currentIndex = 0, lastTrigger = null, doneTimer = null, focusTimer = null, unlockTimer = null;

    function setSelectLabel(radio) {
      selectBtn.classList.remove('is-done');
      selectName.textContent = 'Select ' + (radio.dataset.title || 'this set');
      selectPrice.textContent = radio.dataset.price || '';
    }
    function openDetails(index, trigger) {
      var tpl = document.getElementById('setTpl-' + index);
      var radio = radios[index];
      if (!tpl || !radio) return;
      currentIndex = index;
      lastTrigger = trigger || null;
      clearTimeout(doneTimer); clearTimeout(focusTimer); clearTimeout(unlockTimer);
      modalBody.replaceChildren(tpl.content.cloneNode(true));
      modalBody.scrollTop = 0;
      modalTitle.textContent = radio.dataset.title || '';
      setSelectLabel(radio);
      var sbw = window.innerWidth - document.documentElement.clientWidth;
      if (sbw > 0) document.body.style.paddingRight = sbw + 'px';
      document.body.style.overflow = 'hidden';
      modal.setAttribute('aria-hidden', 'false');
      void modal.offsetWidth;
      modal.classList.add('open');
      focusTimer = setTimeout(function () { try { closeBtn.focus({ preventScroll: true }); } catch (e) { closeBtn.focus(); } }, 60);
    }
    function closeDetails() {
      clearTimeout(doneTimer); clearTimeout(focusTimer); clearTimeout(unlockTimer);
      modal.classList.remove('open');
      modal.setAttribute('aria-hidden', 'true');
      unlockTimer = setTimeout(function () {
        if (!modal.classList.contains('open')) {
          document.body.style.overflow = '';
          document.body.style.paddingRight = '';
          modalBody.replaceChildren();
        }
      }, 300);
      if (lastTrigger && lastTrigger.focus) { try { lastTrigger.focus({ preventScroll: true }); } catch (e) { lastTrigger.focus(); } }
    }

    picker.querySelectorAll('.set-option__more').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        openDetails(parseInt(btn.getAttribute('data-index'), 10), btn);
      });
    });
    closeBtn.addEventListener('click', closeDetails);
    modal.addEventListener('click', function (e) { if (e.target === modal) closeDetails(); });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && modal.classList.contains('open')) closeDetails(); });
    selectBtn.addEventListener('click', function () {
      if (selectBtn.classList.contains('is-done')) return;
      var radio = radios[currentIndex];
      if (!radio) return;
      selectSet(radio);
      selectBtn.classList.add('is-done');
      selectName.textContent = 'Selected ✓';
      selectPrice.textContent = '';
      clearTimeout(doneTimer);
      doneTimer = setTimeout(closeDetails, 700);
    });
    PS.swipeToClose(sheet, [document.getElementById('setModalGrab'), modal.querySelector('.set-modal__head')], closeDetails);
  })();

  /* ---------- SPECIAL OFFERS (card carousel + popup + live values) ---------- */
  (function () {
    var root = document.getElementById('offers');
    var modal = document.getElementById('offersModal');
    if (!root || !modal) return;
    var slides = [].slice.call(root.querySelectorAll('.offers__slide'));
    var bars = [].slice.call(root.querySelectorAll('.offers__bar'));
    var cur = 0;
    var secs = parseInt(root.getAttribute('data-rotate'), 10);
    if (!secs || secs < 2) secs = 5;
    root.style.setProperty('--of-dur', secs + 's');

    var holds = { hover: false, focus: false, touch: false, off: false, modal: false };
    function syncPause() {
      var p = holds.hover || holds.focus || holds.touch || holds.off || holds.modal || document.hidden;
      root.classList.toggle('is-paused', !!p);
    }
    function liveIndexes() { var out = []; slides.forEach(function (s, i) { if (!s.hidden) out.push(i); }); return out; }
    function render() {
      var ids = liveIndexes();
      if (!ids.length) { root.hidden = true; return; }
      root.hidden = false;
      if (ids.indexOf(cur) < 0) cur = ids[0];
      var pos = ids.indexOf(cur);
      slides.forEach(function (s, i) { var on = i === cur; s.classList.toggle('is-active', on); s.setAttribute('aria-hidden', on ? 'false' : 'true'); });
      bars.forEach(function (b, i) {
        var p = ids.indexOf(i);
        b.classList.remove('is-active', 'is-done', 'is-static');
        if (p < 0) return;
        if (p < pos) b.classList.add('is-done');
        else if (p === pos) b.classList.add(ids.length > 1 && !reduceMotion ? 'is-active' : 'is-static');
        b.setAttribute('aria-current', p === pos ? 'true' : 'false');
      });
      root.classList.toggle('is-single', ids.length < 2);
    }
    function next() { var ids = liveIndexes(); if (ids.length < 2) return; cur = ids[(ids.indexOf(cur) + 1) % ids.length]; render(); }
    root.addEventListener('animationend', function (e) {
      if (e.animationName !== 'ofFill') return;
      if (!e.target.closest || !e.target.closest('.offers__bar')) return;
      next();
    });
    bars.forEach(function (b, i) { b.addEventListener('click', function () { cur = i; render(); }); });
    if (window.matchMedia && window.matchMedia('(hover: hover)').matches) {
      root.addEventListener('pointerenter', function (e) { if (e.pointerType === 'mouse') { holds.hover = true; syncPause(); } });
      root.addEventListener('pointerleave', function (e) { if (e.pointerType === 'mouse') { holds.hover = false; syncPause(); } });
    }
    root.addEventListener('focusin', function () { holds.focus = true; syncPause(); });
    root.addEventListener('focusout', function () { holds.focus = false; syncPause(); });
    root.addEventListener('pointerdown', function (e) { if (e.pointerType !== 'mouse') { holds.touch = true; syncPause(); } });
    ['pointerup', 'pointercancel'].forEach(function (t) { root.addEventListener(t, function () { holds.touch = false; syncPause(); }); });
    document.addEventListener('visibilitychange', syncPause);
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) { holds.off = !entries[0].isIntersecting; syncPause(); }, { threshold: 0.2 }).observe(root);
    }

    function setO(key, val) {
      val = val == null ? '' : String(val);
      document.querySelectorAll('[data-o="' + key + '"]').forEach(function (el) {
        if (el.textContent === val) return;
        el.textContent = val;
        if (reduceMotion) return;
        el.classList.remove('of-bump'); void el.offsetWidth; el.classList.add('of-bump');
      });
    }
    function toggleItem(key, on) { document.querySelectorAll('[data-offer-item="' + key + '"]').forEach(function (el) { el.hidden = !on; }); }

    var body = document.getElementById('omBody');
    var chipsWrap = document.getElementById('omChips');
    var chips = [].slice.call(modal.querySelectorAll('.om__chip'));
    function setFilter(f) {
      if (body) body.setAttribute('data-filter', f);
      chips.forEach(function (c) { var on = c.getAttribute('data-filter') === f; c.classList.toggle('is-on', on); c.setAttribute('aria-pressed', on ? 'true' : 'false'); });
    }
    chips.forEach(function (c) { c.addEventListener('click', function () { setFilter(c.getAttribute('data-filter')); if (body) body.scrollTop = 0; }); });
    function refresh() {
      var n = liveIndexes().length;
      setO('count', n + (n === 1 ? ' offer' : ' offers'));
      if (chipsWrap) chipsWrap.hidden = n < 2;
      var f = body ? body.getAttribute('data-filter') : 'all';
      if (f !== 'all') { var chip = modal.querySelector('.om__chip[data-filter="' + f + '"]'); if (!chip || chip.hidden) setFilter('all'); }
      render();
    }
    document.addEventListener('set:change', function (e) {
      var d = e.detail || {};
      var c = d.oCoupon || '', p = d.oPrepaid || '', s = d.oSave || '';
      setO('coupon-save', c); setO('pct', d.oPct || ''); setO('prepaid', p);
      setO('base', d.oBase || ''); setO('best', d.oBest || ''); setO('save', s);
      document.querySelectorAll('[data-o-wrap="save"]').forEach(function (el) { el.hidden = !s; });
      toggleItem('coupon', !!c);
      toggleItem('prepaid', !!p);
      refresh();
    });

    var sheet = modal.querySelector('.om__sheet');
    var closeBtn = document.getElementById('omClose');
    var doneBtn = document.getElementById('omDone');
    var lastTrigger = null, unlockTimer = null, focusTimer = null;
    function isOpen() { return modal.classList.contains('open'); }
    function openModal(trigger) {
      if (isOpen()) return;
      lastTrigger = trigger || null;
      clearTimeout(unlockTimer); clearTimeout(focusTimer);
      if (body) body.scrollTop = 0;
      setFilter('all');
      var sbw = window.innerWidth - document.documentElement.clientWidth;
      if (sbw > 0) document.body.style.paddingRight = sbw + 'px';
      document.body.style.overflow = 'hidden';
      modal.setAttribute('aria-hidden', 'false');
      void modal.offsetWidth;
      modal.classList.add('open');
      holds.modal = true; syncPause();
      focusTimer = setTimeout(function () { try { closeBtn.focus({ preventScroll: true }); } catch (e) { closeBtn.focus(); } }, 60);
    }
    function closeModal() {
      if (!isOpen()) return;
      clearTimeout(focusTimer);
      modal.classList.remove('open');
      modal.setAttribute('aria-hidden', 'true');
      holds.modal = false; syncPause();
      unlockTimer = setTimeout(function () { if (!isOpen()) { document.body.style.overflow = ''; document.body.style.paddingRight = ''; } }, 300);
      if (lastTrigger && lastTrigger.focus) { try { lastTrigger.focus({ preventScroll: true }); } catch (e) { lastTrigger.focus(); } }
    }
    root.querySelectorAll('[data-offers-open]').forEach(function (btn) { btn.addEventListener('click', function (e) { e.preventDefault(); openModal(btn); }); });
    closeBtn.addEventListener('click', closeModal);
    if (doneBtn) doneBtn.addEventListener('click', closeModal);
    modal.addEventListener('click', function (e) { if (e.target === modal) closeModal(); });
    document.addEventListener('keydown', function (e) {
      if (!isOpen()) return;
      if (e.key === 'Escape') { closeModal(); return; }
      if (e.key === 'Tab') PS.trapFocus(e, sheet);
    });
    PS.swipeToClose(sheet, [document.getElementById('omGrab'), document.getElementById('omHead')], closeModal);
    render();
    syncPause();
  })();

  /* ---------- STOCK URGENCY COUNTDOWN (10 min, restarts each new session) ---------- */
  (function () {
    var minEl = document.getElementById('stockTimerMin');
    var secEl = document.getElementById('stockTimerSec');
    if (!minEl || !secEl) return;
    var DURATION = 10 * 60 * 1000;
    var KEY = 'stockTimerEnd';
    function pad(n) { return n < 10 ? '0' + n : '' + n; }
    function getEnd() {
      var end = 0;
      try { end = parseInt(sessionStorage.getItem(KEY), 10) || 0; } catch (e) {}
      if (!end || end <= Date.now()) {
        end = Date.now() + DURATION;
        try { sessionStorage.setItem(KEY, end); } catch (e) {}
      }
      return end;
    }
    var end = getEnd();
    function setDigit(el, val) {
      if (el.textContent === val) return;
      el.textContent = val;
      if (reduceMotion) return;
      el.classList.remove('is-tick'); void el.offsetWidth; el.classList.add('is-tick');
    }
    function tick() {
      var left = end - Date.now();
      if (left <= 0) { end = getEnd(); left = end - Date.now(); }
      var s = Math.max(0, Math.ceil(left / 1000));
      setDigit(minEl, pad(Math.floor(s / 60)));
      setDigit(secEl, pad(s % 60));
    }
    var timerId = null;
    function start() { if (timerId) return; tick(); timerId = setInterval(tick, 1000); }
    function stop() { clearInterval(timerId); timerId = null; }
    document.addEventListener('visibilitychange', function () { if (document.hidden) stop(); else start(); });
    start();
  })();

  /* =====================================================================
     DEFERRED: runs when the browser is idle (max 1.2 s)
     ===================================================================== */
  idle(function () {

    /* pause decorative looping animations while off screen */
    if ('IntersectionObserver' in window) {
      var deco = document.querySelectorAll('.btn-add-to-cart, #enqOpen, .product-tag-badge, .offers__save, .stock-urgency-box, .set-option__badge, .m3d-btn');
      if (deco.length) {
        var decoIo = new IntersectionObserver(function (entries) {
          entries.forEach(function (en) { en.target.classList.toggle('is-offscreen', !en.isIntersecting); });
        });
        deco.forEach(function (el) { decoIo.observe(el); });
      }
    }

    /* desktop: both columns follow the scroll (sticky `top` per column) */
    (function () {
      var wrap = document.querySelector('.product-split');
      if (!wrap || !window.matchMedia) return;
      var cols = [wrap.querySelector('.product-split__media'), wrap.querySelector('.product-split__info')].filter(Boolean);
      if (cols.length < 2) return;
      var mq = window.matchMedia('(min-width: 901px)');
      var GAP = 12, raf = 0;
      function apply() {
        raf = 0;
        if (!mq.matches) { cols.forEach(function (c) { c.style.top = ''; }); return; }
        var vh = window.innerHeight;
        var tops = cols.map(function (c) { return Math.min(GAP, vh - c.offsetHeight - GAP) + 'px'; });
        cols.forEach(function (c, i) { if (c.style.top !== tops[i]) c.style.top = tops[i]; });
      }
      function queue() { if (!raf) raf = requestAnimationFrame(apply); }
      apply();
      window.addEventListener('resize', queue, { passive: true });
      if (mq.addEventListener) mq.addEventListener('change', queue);
      if ('ResizeObserver' in window) { var ro = new ResizeObserver(queue); cols.forEach(function (c) { ro.observe(c); }); }
      window.addEventListener('load', queue);
    })();

    /* sticky cart bar (phones): shown while the Shiprocket button (or, in enquiry mode, the Enquire button) is off screen */
    var shiprocketEl = document.getElementById('sr_one') || document.getElementById('enqOpen');
    var stickyCartBar = document.getElementById('stickyCartBar');
    if (shiprocketEl && stickyCartBar && 'IntersectionObserver' in window && window.matchMedia) {
      var srVisible = false;
      var mobileMq = window.matchMedia('(max-width: 900px)');
      var setBarVisibility = function () {
        var want = !mobileMq.matches ? 'none' : (srVisible ? 'none' : 'flex');
        if (stickyCartBar.style.display !== want) stickyCartBar.style.display = want;
      };
      new IntersectionObserver(function (entries) { srVisible = entries[0].isIntersecting; setBarVisibility(); }, { threshold: 0 }).observe(shiprocketEl);
      if (mobileMq.addEventListener) mobileMq.addEventListener('change', setBarVisibility);
    }
  }, 1200);
})();